import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Save, Upload, Trash2, Video, Eye } from "lucide-react";

const FIELDS = [
  ["title", "Lesson title", "input"],
  ["action_title", "Action title (short)", "input"],
  ["action_text", "Your action this week", "textarea", 3],
  ["means", "What it means", "textarea", 3],
  ["why_later", "Why it matters later", "textarea", 3],
  ["closing", "Closing message", "input"],
  ["script", "Founder script / transcript", "textarea", 14],
];

function LessonEditor({ lesson, onSaved }) {
  const [form, setForm] = useState(lesson);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => setForm(lesson), [lesson]);
  const dirty = FIELDS.some(([k]) => (form[k] ?? "") !== (lesson[k] ?? "")) || (form.video_url ?? "") !== (lesson.video_url ?? "");

  const save = async () => {
    setBusy(true);
    try {
      const patch = Object.fromEntries([...FIELDS.map(([k]) => k), "video_url"].filter((k) => (form[k] ?? "") !== (lesson[k] ?? "")).map((k) => [k, form[k] ?? ""]));
      const { data } = await api.patch(`/admin/lessons/${lesson.week_number}`, patch);
      toast.success(`Week ${lesson.week_number} saved (rev ${data.revision})`); onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/admin/lessons/${lesson.week_number}/video`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success("Video attached"); onSaved((await api.get("/admin/lessons")).data.find((l) => l.week_number === lesson.week_number));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeVideo = async () => {
    setBusy(true);
    try { await api.delete(`/admin/lessons/${lesson.week_number}/video`); toast.success("Video removed"); onSaved({ ...lesson, video_url: null, video_filename: null }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-5" data-testid={`lesson-editor-${lesson.week_number}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Week {lesson.week_number} · {lesson.phase} · rev {lesson.revision}</div>
          <div className="font-head uppercase text-xl mt-1">{form.title}</div>
        </div>
        <Link to={`/app/lesson/${lesson.week_number}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest border border-border px-3 py-2 rounded hover:bg-muted" data-testid={`lesson-preview-${lesson.week_number}`}><Eye size={12} /> Preview</Link>
      </div>

      <div className="rounded-md border border-dashed border-border p-4" data-testid={`lesson-video-panel-${lesson.week_number}`}>
        <div className="flex items-center gap-2 label-eyebrow"><Video size={12} /> Founder video</div>
        {lesson.video_url ? (
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm truncate">{lesson.video_filename || lesson.video_url}</div>
            <button onClick={removeVideo} disabled={busy} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-destructive hover:underline" data-testid={`lesson-video-remove-${lesson.week_number}`}><Trash2 size={12} /> Remove</button>
          </div>
        ) : <div className="mt-2 text-xs text-muted-foreground">No video yet — members see the transcript. Upload an MP4 (≤80MB) or paste a hosted URL below.</div>}
        <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2">
          <input value={form.video_url ?? ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://… .mp4 or hosted video URL"
                 className="rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[44px]" data-testid={`lesson-video-url-${lesson.week_number}`} />
          <label className={`inline-flex items-center justify-center gap-2 border border-border px-4 rounded-md text-[11px] uppercase tracking-widest cursor-pointer hover:bg-muted min-h-[44px] ${busy ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload size={12} /> Upload file
            <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`lesson-video-file-${lesson.week_number}`} />
          </label>
        </div>
      </div>

      {FIELDS.map(([k, label, type, rows]) => (
        <div key={k}>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</label>
          {type === "textarea"
            ? <textarea rows={rows} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed" data-testid={`lesson-field-${k}-${lesson.week_number}`} />
            : <input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[44px]" data-testid={`lesson-field-${k}-${lesson.week_number}`} />}
        </div>
      ))}

      <button onClick={save} disabled={!dirty || busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 min-h-[44px]" data-testid={`lesson-save-${lesson.week_number}`}>
        <Save size={14} /> {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
      </button>
    </div>
  );
}

export default function AdminLessons() {
  const [lessons, setLessons] = useState([]);
  const [week, setWeek] = useState(1);
  const load = async () => { try { setLessons((await api.get("/admin/lessons")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);
  const current = lessons.find((l) => l.week_number === week);

  return (
    <div className="space-y-6" data-testid="admin-lessons-page">
      <Link to="/app/admin" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-primary"><ArrowLeft size={14} /> Admin</Link>
      <div>
        <div className="label-eyebrow">Curriculum CMS</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Longevity Lessons.</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">Edit each week's lesson text and attach the founder video when it's filmed. Changes go live for members immediately.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="lesson-week-tabs">
        {lessons.map((l) => (
          <button key={l.week_number} onClick={() => setWeek(l.week_number)} data-testid={`lesson-tab-${l.week_number}`}
            className={`min-h-[44px] px-3 rounded-md text-[11px] font-head uppercase tracking-wider border ${week === l.week_number ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}>
            W{l.week_number}{l.video_url ? " ●" : ""}
          </button>
        ))}
      </div>
      {current && <LessonEditor lesson={current} onSaved={(d) => setLessons((prev) => prev.map((l) => l.week_number === d.week_number ? d : l))} />}
    </div>
  );
}
